/**
 * Centralized presigned-URL resolution with caching and request dedup.
 *
 * Replaces the three legacy per-component fetchers (LazyImage / ImageWithUrl /
 * VideoWithUrl), each of which re-fetched independently with no cache and fell
 * back to random placeholder images on error.
 *
 *  - One in-flight request per key (dedup).
 *  - Cached URLs expire slightly before the server TTL so we never hand out a
 *    URL that's about to 403.
 *  - Real error state surfaced to the caller (no silent placeholder).
 */
import { useEffect, useRef, useState } from 'react';
import { getDownloadUrl } from '../lib/apiClient.ts';
import { PRESIGNED_URL_CONFIG, CDN_BASE_URL } from '../config/awsConfig.ts';

// Refresh 5 minutes before the server-side expiry.
const TTL_MS = (PRESIGNED_URL_CONFIG.EXPIRES_IN_SECONDS - 300) * 1000;

interface CacheEntry {
  url: string;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<string>>();

/** Monotonic clock; tests can rely on Date.now via fake timers. */
function now(): number {
  return Date.now();
}

/**
 * Build a direct CDN URL for a key (encoding each path segment so event names
 * with spaces/special chars work). Returns null when no CDN is configured.
 */
export function cdnUrl(key: string): string | null {
  if (!CDN_BASE_URL) return null;
  const encoded = key.split('/').map(encodeURIComponent).join('/');
  return `${CDN_BASE_URL.replace(/\/$/, '')}/${encoded}`;
}

/** Resolve a GET URL for a key. Uses the CDN directly when configured. */
export async function resolveSignedUrl(key: string, signal?: AbortSignal): Promise<string> {
  const direct = cdnUrl(key);
  if (direct) return direct;

  const cached = cache.get(key);
  if (cached && cached.expiresAt > now()) return cached.url;

  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = getDownloadUrl(key, signal)
    .then((url) => {
      cache.set(key, { url, expiresAt: now() + TTL_MS });
      inFlight.delete(key);
      return url;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, promise);
  return promise;
}

/** Drop a key from the cache (e.g. after a move invalidates it). */
export function invalidateSignedUrl(key: string): void {
  cache.delete(key);
  inFlight.delete(key);
}

/** Clear the entire cache (tests). */
export function _clearSignedUrlCache(): void {
  cache.clear();
  inFlight.clear();
}

export interface SignedUrlState {
  url: string | null;
  loading: boolean;
  error: Error | null;
  /** Force a re-fetch (clears the cached entry for this key). */
  retry: () => void;
}

/**
 * React hook: resolve the presigned URL for `key`.
 * Pass `enabled=false` to defer fetching (e.g. until scrolled into view).
 */
export function useSignedUrl(key: string | null, enabled = true): SignedUrlState {
  const [url, setUrl] = useState<string | null>(() =>
    key ? (cdnUrl(key) ?? cache.get(key)?.url ?? null) : null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!key || !enabled) return;

    // CDN path: the URL is deterministic — set it synchronously, no fetch.
    const direct = cdnUrl(key);
    if (direct) {
      setUrl(direct);
      setError(null);
      return;
    }

    const fresh = cache.get(key);
    if (fresh && fresh.expiresAt > now()) {
      setUrl(fresh.url);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    resolveSignedUrl(key, controller.signal)
      .then((resolved) => {
        if (!mounted.current || controller.signal.aborted) return;
        setUrl(resolved);
      })
      .catch((err: unknown) => {
        if (!mounted.current || controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (mounted.current && !controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [key, enabled, attempt]);

  return {
    url,
    loading,
    error,
    retry: () => {
      if (key) invalidateSignedUrl(key);
      setAttempt((n) => n + 1);
    },
  };
}
